<style lang="stylus">
/* PayPal Integration Styles */
.paypal-wrapper {
  position: absolute;
  border-style: solid;
  border-color: #fec627;
  padding: 4px 5px 4px 12px;
  width: 100%;
  opacity: 0.01;
  z-index: 1;
  pointer-events: all !important;
}

.paypal-button {
  transform: scaleX(1.1) scaleY(1.3);
}

/* Final Grade Checkbox */
.final-grade-checkbox {
  .v-label {
    font-size: 14px;
    margin-right: auto;
  }
}

/* Grade Change Error Modal */
.gradeChangeError {
  font-weight: bold;
  font-size: 22px;
  color: #a30046;
  line-height: 2rem !important;
}

.voucher-tag {
  display: inline-flex;
  align-items: center;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #444444;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 8px;
  max-width: fit-content;

  span {
    margin-right: 8px;

    &:before {
      content: '✓';
      color: #2ba206;
      margin-right: 5px;
      font-weight: bold;
    }
  }
}

.savings-text {
  font-size: 13px;
  color: #2ba206;
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 500;
  display: flex;
  align-items: center;
  line-height: 1.4;

  &:before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    min-width: 16px; /* Prevent shrinking */
    margin-right: 5px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232ba206'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/%3E%3C/svg%3E");
    background-size: contain;
  }
}

.voucher-input {
  .v-input__slot {
    padding: 10px !important;
  }

  :deep(.v-text-field__slot input) {
    font-size: 14px;
    height: 40px;
  }

  :deep(.v-label) {
    display: none !important;
  }
}

/* Loading Overlay Styles */
.checkout-selected-course-wrapper {
  position: relative;
}

.loading-content {
  /* Just the spinner, no background or padding needed */
}

.content-hidden {
  opacity: 0;
  pointer-events: none;
}

.checkout-selected-content {
  transition: opacity 0.3s ease;
}

.v-selection-control__input {
  justify-content: flex-start !important;
}
</style>

<template>
  <div class="checkout-selected-course-wrapper">
    <!-- Main Content -->
    <div class="checkout-selected-content" :class="{ 'content-hidden': activeLoading }">
      <!-- Card Layout -->
      <SelectedCourseCard
        title="Selected Courses"
        :courses="courseData"
        :show-total="true"
        :total-label="isPrepaidCourseAmount ? '' : 'TOTAL'"
      >
        <!-- Custom total price with voucher support -->
        <template #total-price>
          <template v-if="isPrepaidCourseAmount"> Prepaid </template>
          <template v-else-if="voucherApplied">
            <span
              style="
                text-decoration: line-through;
                font-size: 14px;
                color: #888;
                margin-right: 5px;
                font-weight: normal;
              "
              >${{ amount }}</span
            >
            <span style="color: #2ba206">${{ discountedAmount }}</span>
          </template>
          <template v-else> ${{ amount }} </template>
        </template>

        <!-- Success icon -->
        <template #total-icon>
          <v-icon v-if="successMsg" color="#2ba206" size="22">mdi-check-circle</v-icon>
        </template>

        <!-- Additional content: Voucher, Buttons, Checkbox -->
        <template #additional-content>
          <!-- Voucher Applied Section -->
          <div v-if="voucherApplied" class="mt-3">
            <template v-if="!isInvisibleVoucher">
              <div style="font-size: 14px; font-weight: 600; color: #444444; margin-bottom: 8px">Voucher Applied</div>
              <div class="voucher-tag">
                <span>{{ voucherCode }}</span>
                <v-icon small @click="removeVoucher" v-if="!successMsg" style="cursor: pointer; color: #777"
                  >mdi-close-circle</v-icon
                >
              </div>
            </template>
            <div v-if="savingsInfo" class="savings-text mt-2">
              You saved ${{ savingsInfo.amount }} ({{ savingsInfo.percentage }}% off)
            </div>
          </div>

          <!-- Voucher Input Section -->
          <div
            v-if="!voucherApplied && !successMsg && showApplyVoucherForSpecificPartners && !isInvisibleVoucher"
            class="mt-4 mb-4"
          >
            <div
              class="mb-2"
              style="font-size: 14px; font-weight: 600; color: #444444; display: flex; align-items: center"
            >
              Voucher Code
              <span style="font-size: 12px; font-weight: 400; color: #666666; margin-left: 6px; white-space: nowrap"
                >(one time per order)</span
              >
            </div>

            <div style="position: relative; width: 100%">
              <v-text-field
                v-model="voucherCode"
                placeholder="Enter code"
                outlined
                dense
                background-color="white"
                :error="!!voucherErrorMsg"
                :disabled="voucherLoading"
                @keyup.enter="applyVoucher"
                style="border-radius: 4px; font-size: 14px"
                :hide-details="true"
                class="voucher-input"
              />
              <div
                style="
                  position: absolute;
                  right: 14px;
                  top: 50%;
                  transform: translateY(-50%);
                  color: #fec627;
                  font-weight: 700;
                  font-size: 14px;
                  cursor: pointer;
                  user-select: none;
                  letter-spacing: 0.5px;
                  border-radius: 4px;
                  transition: background-color 0.2s;
                "
                :style="[
                  !voucherCode || voucherLoading ? 'opacity: 0.5; cursor: not-allowed;' : '',
                  'background-color: ' + (isHoveringApply ? 'rgba(254, 198, 39, 0.1)' : 'transparent')
                ]"
                @click="voucherCode && !voucherLoading && applyVoucher()"
                @mouseenter="isHoveringApply = true"
                @mouseleave="isHoveringApply = false"
              >
                <span style="color: rgb(68 68 68)" v-if="!voucherLoading">APPLY</span>
                <v-progress-circular v-else indeterminate size="16" width="2" color="#fec627" />
              </div>
              <div v-if="voucherErrorMsg" style="color: #ff5252; font-size: 12px; margin-top: 8px">
                {{ voucherErrorMsg }}
              </div>
            </div>
          </div>

          <!-- Continue Button Section -->
          <div
            class="mt-4"
            :style="[
              buttonLabel === 'Add to Transcript' && !finalGradeCheck ? 'filter: opacity(0.5);' : '',
              voucherLoading ? 'pointer-events: none;' : ''
            ]"
          >
            <div
              v-if="transactionStatus !== 'Success' && continueToPayOrPrepay && !voucherLoading"
              style="position: relative"
            >
              <paypal-checkout
                v-if="
                  !isStripePaymentEnabled &&
                  transactionStatus !== 'Success' &&
                  !isPrepaidCourseAmount &&
                  continueToPayOrPrepay &&
                  (!discountedAmount || parseFloat(discountedAmount) > 0) &&
                  (!amount || parseFloat(amount) > 0) &&
                  (buttonLabel !== 'Add to Transcript' || !courseInfo?.campaignEligible || campaignCheckPassed)
                "
                v-show="(['Add to Transcript'].indexOf(buttonLabel) > -1 ? finalGradeCheck : true) && !voucherLoading"
                :amount="discountedAmount || amount"
                :invoice-number="invoiceNumber"
                currency="USD"
                :client="credentials"
                :env="env"
                v-on:payment-authorized="paymentAuthorized"
                v-on:payment-completed="paymentCompleted"
                v-on:payment-cancelled="paymentCancelled"
                v-on:payment-error="paymentError"
                :items="courseCheckoutItems"
                class="paypal-wrapper"
                :button-style="paypalButtonStyle"
                target="_blank"
                :courseTitle="(coursePrefixDisplayLabel || coursePrefix) + ' ' + title"
                :courseName="title"
                :coursePrefix="coursePrefix"
                :courseStartDate="courseStartDate"
                :courseEndDate="courseEndDate"
                :offeringId="courseOfferingId"
              />
              <UiButton
                variant="gold"
                size="large"
                :disabled="
                  voucherLoading ||
                  campaignCheckLoading ||
                  (['Add to Transcript'].indexOf(buttonLabel) > -1 && !finalGradeCheck)
                "
                :loading="campaignCheckLoading"
                @click="handleContinueClick"
                style="width: 100%"
              >
                <template v-if="campaignCheckLoading"> Checking... </template>
                <template v-else> Continue to payment </template>
              </UiButton>
            </div>
          </div>

          <!-- Final Grade Checkbox -->
          <v-checkbox
            v-if="['Add to Transcript'].indexOf(buttonLabel) > -1"
            class="final-grade-checkbox mt-4"
            v-model="finalGradeCheck"
            label="I understand that this is my final grade, and it can not be changed once I transcript the course."
            @change="asuoDataLayerClick('courseCheckout', '1')"
          />
        </template>
      </SelectedCourseCard>
    </div>

    <credit-request-from
      v-if="shouldLoadCreditRequestForm"
      :creditRequestRequestUUID="creditRequestRequestUUID"
      :creditRequestTimestamp="creditRequestTimestamp"
      :creditRequestCourseOrg="creditRequestCourseOrg"
      :creditRequestCourseNum="creditRequestCourseNum"
      :creditRequestCourseRun="creditRequestCourseRun"
      :creditRequestEnrollmentTimestamp="creditRequestEnrollmentTimestamp"
      :creditRequestCourseCompletionTimestamp="creditRequestCourseCompletionTimestamp"
      :creditRequestFinalGrade="creditRequestFinalGrade"
      :creditRequestUserUsername="creditRequestUserUsername"
      :creditRequestUserEmail="creditRequestUserEmail"
      :creditRequestUserFullName="creditRequestUserFullName"
      :creditRequestUserMailingAddress="creditRequestUserMailingAddress"
      :creditRequestUserCountry="creditRequestUserCountry"
      :creditRequestSignature="creditRequestSignature"
    />

    <ConfirmationModal
      v-model:show="showGradeOrModeError"
      title="Request Error"
      :message="creditConvertModelError"
      confirm-text="Close"
      cancel-text=""
      confirm-variant="gold"
      max-width="500"
      header-style="background-color: #fec627; font-size: 20px;"
      @confirm="closeGradeChangeError"
    />

    <v-dialog v-model="showPaymentError" width="500" class="dialogClass">
      <v-card style="border-bottom: 8px solid #ffc627; text-align: center; background-color: white">
        <v-spacer style="height: 30px" />

        <v-col cols="12" sm="12" md="12" class="gradeChangeError">
          {{ errorMsg }}
          <span>
            Please<a
              :href="ulcPartnerFlag ? 'https://ulc.asu.edu/contact-us/' : 'https://ea.asu.edu/contact/'"
              target="_blank"
            >
              contact our support team</a
            >
            for assistance.
          </span>
        </v-col>
        <v-spacer style="height: 20px" />

        <v-col cols="10" sm="6" md="1" class="closeDialog">
          <v-btn style="background-color: #fec627; color: black; z-index: 999" @click="closeGradeChangeError()"
            >Close</v-btn
          >
        </v-col>
        <v-spacer style="height: 30px" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { useUserStore } from '@/stores/user';
import { useCheckoutStore } from '@/stores/checkout';
import { isFeatureEnabled } from '../../feature-flags';

/* eslint-disable no-console */
import { DataLayerMixin } from '../mixins/dataLayerMixin';
import { Logger } from '../mixins/logger';
import { Helper } from '../mixins/helpers.js';
import { AjaxLoader } from '../mixins/ajaxLoader';
import CanvasModel from '../data_models/canvas_user.model';

import CreditRequestService from '../services/credit-request.service';
import PaypalCheckout from './PaypalCheckout.vue';
import connectorService from '../services/connector.service.js';
import { Button as UiButton, SelectedCourseCard, ConfirmationModal } from '@/components/ui';

export default defineComponent({
  emits: ['next', 'payment-processing', 'payment-complete', 'promo-validated', 'promo-removed'],
  name: 'checkoutSelectedCourse',

  components: {
    'paypal-checkout': PaypalCheckout,
    UiButton,
    SelectedCourseCard,
    ConfirmationModal
  },

  data() {
    return {
      checkoutStore: null,
      shouldLoadCreditRequestForm: false,
      creditRequestRequestUUID: '',
      creditRequestTimestamp: 0,
      creditRequestCourseOrg: '',
      creditRequestCourseNum: '',
      creditRequestCourseRun: '',
      creditRequestEnrollmentTimestamp: 0,
      creditRequestCourseCompletionTimestamp: 0,
      creditRequestFinalGrade: '',
      creditRequestUserUsername: '',
      creditRequestUserEmail: '',
      creditRequestUserFullName: '',
      creditRequestUserMailingAddress: '',
      creditRequestUserCountry: '',
      creditRequestSignature: '',
      creditConvertModelError: '',
      paypalButtonStyle: {
        label: 'checkout',
        size: 'medium',
        shape: 'pill',
        color: 'blue'
      },
      whiteOut: false,
      amount: '',
      discountedAmount: null,
      length: '',
      credentials: {
        sandbox: import.meta.env.VITE_PAYPAL_CLIENT_ID,
        production: import.meta.env.VITE_PAYPAL_CLIENT_ID
      },
      courseCheckoutItems: [
        {
          name: '', // Will be set in created() after checkout store is available
          description: '', // Will be set in created() after checkout store is available
          quantity: '1',
          price: '',
          currency: 'USD'
        }
      ],
      transactionStatus: '',
      finalGradeCheck: false,
      transactions: [],
      continueToPayOrPrepay: false,
      showGradeOrModeError: false,
      gradeChanged: false,
      updatedGrade: '',
      localFinalGrade: '',
      errorMsg: '',
      showPaymentError: false,
      // Voucher related fields
      voucherCode: '',
      voucherApplied: false,
      voucherLoading: false,
      voucherErrorMsg: '',
      isHoveringApply: false,
      // Campaign check state (ULC-2637)
      campaignCheckPassed: false,
      campaignCheckLoading: false,
      // ULC-2669: Invisible voucher flag (auto-applied, cannot be removed)
      isInvisibleVoucher: false,
      // Aether promo code (validated, ready to apply on Stripe session)
      aetherPromoCode: '',
      aetherPromoDetails: null
    };
  },

  computed: {
    userStore() {
      return useUserStore();
    },
    // Format course data for SelectedCourseCard component
    courseData() {
      const course = {
        coursePrefix: this.coursePrefixDisplayLabel || this.coursePrefix,
        courseName: this.title,
        credits: this.credits || 3,
        price: this.isPrepaidCourseAmount
          ? 0
          : this.buttonAction === 'Add to Transcript'
            ? this.creditConversionPrice
            : this.creditEligibilityPrice
      };

      // Add either startDate or finalGrade based on button label
      if (['Upgrade', 'Access Course', 'Enroll and pay'].indexOf(this.buttonLabel) > -1) {
        course.startDate = this.getStartDate();
      } else if (this.buttonLabel === 'Add to Transcript') {
        course.finalGrade = `${this.localFinalGrade || this.finalGrade} / ${Math.round(this.grade * 100)}`;
      }

      return [course];
    },
    // Calculate savings amount and percentage when voucher is applied
    savingsInfo() {
      if (!this.voucherApplied || !this.discountedAmount) return null;

      const originalAmount = parseFloat(this.amount);
      const discountedAmount = parseFloat(this.discountedAmount);
      const savingsAmount = originalAmount - discountedAmount;
      const savingsPercentage = (savingsAmount / originalAmount) * 100;

      return {
        amount: savingsAmount.toFixed(2),
        percentage: Math.round(savingsPercentage)
      };
    },

    gradeValue() {
      const gradeValue = this.gradeChanged ? this.updatedGrade : this.grade;
      // Convert to number if it's a string number, otherwise return as is
      return !isNaN(gradeValue) && gradeValue !== '' ? Number(gradeValue) : gradeValue;
    },

    invoiceNumber() {
      let today = new Date().toISOString().slice(0, 10).replace(/-/g, '').slice(2);

      let prefix = import.meta.env.VITE_ENV_HOOK === 'prod' ? 'PRODOSPP' : 'OSPP';

      let random2Char = '';
      let random3Char = '';
      let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let charactersLength = characters.length;

      for (let i = 0; i < 2; i++) {
        random2Char += characters.charAt(Math.floor(Math.random() * charactersLength));
      }

      for (let i = 0; i < 3; i++) {
        random3Char += characters.charAt(Math.floor(Math.random() * charactersLength));
      }

      return `${prefix}-${today}${Math.floor(Math.random() * 10)}${random2Char}${Math.floor(Math.random() * 100)}${random3Char}`;
    },

    creditRequest() {
      return import.meta.env.VITE_CREDIT_REQUEST_URL;
    },

    env() {
      return import.meta.env.VITE_ENV_HOOK === 'prod' ? 'production' : 'sandbox';
    },

    isPrepaidCourseAmount() {
      const coursesStore = useCoursesStore();
      if (coursesStore.enrolledCourses) {
        return (
          (['Add to Transcript'].indexOf(this.buttonAction) > -1 && ['1', '3', '5'].indexOf(this.benefitType) > -1) ||
          (['UpgradeModal', 'Upgrade', 'Enroll and pay'].indexOf(this.buttonAction) > -1 &&
            ['1', '2', '5'].indexOf(this.benefitType) > -1 &&
            this.multiEnrollmentType !== 'LearnerPay')
        );
      }
      return false;
    },
    ulcPartnerFlag() {
      return Helper.methods.isUlcPartner();
    },
    // Show voucher input only when backend allowlist (DynamoDB: VOUCHER_ALLOWED_PARTNERS) includes the learner's partner.
    showApplyVoucherForSpecificPartners() {
      const userStore = useUserStore();
      return userStore.apis.learnerData?._isVoucherAllowed === true;
    },

    // Get all data from checkout store instead of props
    title() {
      return this.checkoutStore?.title || '';
    },
    coursePrefix() {
      return this.checkoutStore?.coursePrefix || '';
    },
    coursePrefixDisplayLabel() {
      return this.checkoutStore?.coursePrefixDisplayLabel || '';
    },
    courseType() {
      return this.checkoutStore?.courseType || '';
    },
    courseStartDate() {
      return this.checkoutStore?.courseStartDate || '';
    },
    courseEndDate() {
      return this.checkoutStore?.courseEndDate || '';
    },
    currentGrade() {
      return this.checkoutStore?.currentGrade || '';
    },
    benefitsEligible() {
      return this.checkoutStore?.benefitsEligible || false;
    },
    buttonLabel() {
      return this.checkoutStore?.buttonLabel || '';
    },
    benefitType() {
      return this.checkoutStore?.benefitType || '';
    },
    finalGrade() {
      return this.checkoutStore?.finalGrade || '';
    },
    buttonAction() {
      return this.checkoutStore?.buttonAction || '';
    },
    courseCompletedDate() {
      return this.checkoutStore?.courseCompletedDate || '';
    },
    courseOfferingId() {
      return this.checkoutStore?.courseOfferingId || '';
    },
    courseOfferingName() {
      return this.checkoutStore?.courseOfferingName || '';
    },
    courseInfo() {
      return this.checkoutStore?.courseInfo || {};
    },
    enrollmentDate() {
      return this.checkoutStore?.enrollmentDate || '';
    },
    creditConversionPrice() {
      return this.checkoutStore?.creditConversionPrice || 0;
    },
    creditEligibilityPrice() {
      return this.checkoutStore?.creditEligibilityPrice || 0;
    },
    canvasEnrollmentId() {
      return this.checkoutStore?.canvasEnrollmentId || null;
    },
    canvasUserId() {
      return this.checkoutStore?.canvasUserId || null;
    },
    canvasCourseId() {
      return this.checkoutStore?.canvasCourseId || null;
    },
    lms() {
      return this.checkoutStore?.lms || '';
    },
    from() {
      return this.checkoutStore?.from || '';
    },
    enrollmentId() {
      return this.checkoutStore?.enrollmentId || '';
    },
    credits() {
      return this.checkoutStore?.credits || 0;
    },
    grade() {
      return this.checkoutStore?.grade || 0;
    },
    enrollmentType() {
      return this.checkoutStore?.enrollmentType || '';
    },
    multiEnrollmentType() {
      return this.checkoutStore?.multiEnrollmentType || '';
    },
    // The enrollments API expects the frontend enum, while the store holds the
    // DB-style value. Omitted when unknown so the server keeps its own default
    // rather than being told the wrong thing (ULC-3157).
    apiEnrollmentType() {
      return (
        {
          LearnerPay: 'EnrollmentTypeLearnerPay',
          PartnerPay: 'EnrollmentTypePartnerPay',
          Both: 'EnrollmentTypeBoth'
        }[this.multiEnrollmentType] || undefined
      );
    },
    // Mirrors the amount conditions gating the PayPal button: a learner owes
    // something unless a voucher or partner coverage brought it to zero.
    hasAmountDue() {
      return (
        (!this.discountedAmount || parseFloat(this.discountedAmount) > 0) &&
        (!this.amount || parseFloat(this.amount) > 0)
      );
    },
    isStripePaymentEnabled() {
      return this.userStore?.apis?.learnerData?.useAetherFlow || false;
    }
  },

  mixins: [DataLayerMixin, Helper, AjaxLoader, Logger],

  props: {
    // UI state prop - not part of checkout data
    successMsg: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    // Voucher related methods
    promoErrorMessage(result) {
      // Map Aether promo error codes to learner-friendly copy; fall back to the
      // server message, then a generic one.
      const messagesByCode = {
        STR_4001: "This promo code isn't valid.",
        STR_4002: "This code isn't available for your account.",
        STR_4003: 'This code is only valid on your first purchase.',
        STR_4004: 'You have already used this code the maximum number of times.',
        STR_4005: 'This code is not valid for this type of purchase.',
        STR_4006: "This code isn't valid for the selected course(s)."
      };
      return messagesByCode[result?.errorCode] || result?.message || 'Invalid promo code';
    },
    async applyVoucher() {
      if (!this.voucherCode || this.voucherLoading || !this.amount) {
        this.voucherErrorMsg = !this.voucherCode
          ? 'Please enter a voucher code'
          : !this.amount
            ? 'Unable to apply voucher - no amount available'
            : '';
        return;
      }

      this.voucherLoading = true;
      this.voucherErrorMsg = '';

      try {
        // Use Aether promo code validation when Stripe flow is active
        if (this.isStripePaymentEnabled) {
          const purchaseType = this.buttonAction === 'Add to Transcript' ? 'credit_conversion' : 'enrollment';
          const result = await this.$conn.validateAetherPromoCode({
            promoCode: this.voucherCode,
            benefitType: this.benefitType,
            purchaseType,
            courseOfferingId: this.courseOfferingId,
            learnerId: this.userStore.apis.learnerData?.learnerId,
            term: this.courseInfo?.term,
            partnerName: this.userStore.apis.learnerData?._partnerName
          });

          // API returns { status: 'success', data: '...' } on success
          // Connector catch block returns { valid: false, message: '...' } on error
          const isValid = result?.status === 'success' || result?.valid;

          if (isValid) {
            this.aetherPromoCode = this.voucherCode;
            this.aetherPromoDetails = result;
            this.voucherApplied = true;

            // Preview the discount locally from the validated coupon so the total reflects it on apply
            // (mirrors the invisible-voucher path). Stripe still applies the authoritative discount at payment.
            const promoData = result?.data || result;
            if (promoData?.coupon) {
              let finalAmount = parseFloat(this.amount);
              if (promoData.coupon.amount_off) {
                finalAmount = Math.max(0, finalAmount - promoData.coupon.amount_off / 100);
              }
              if (promoData.coupon.percent_off) {
                finalAmount = Math.max(0, finalAmount - (finalAmount * promoData.coupon.percent_off) / 100);
              }
              this.discountedAmount = finalAmount.toFixed(2);
              this.courseCheckoutItems[0].price = this.discountedAmount;
            }

            this.courseCheckoutItems[0].description = `${this.title} (Voucher: ${this.voucherCode})`;

            this.$emit('promo-validated', {
              code: this.voucherCode
            });

            this.logInfo('Aether promo code validated', {
              code: this.voucherCode
            });
          } else {
            this.voucherErrorMsg = this.promoErrorMessage(result);
            this.voucherCode = '';
          }
        } else {
          // Legacy PayPal voucher validation flow
          const response = await this.$conn.validateVoucherCode({
            voucherCode: this.voucherCode,
            courseOfferingId: this.courseOfferingId,
            enrollmentId: this.enrollmentId,
            enrollmentMode: this.courseInfo?._enrollmentMode,
            learnerId: this.userStore.apis.learnerData?.learnerId,
            benefitType: this.benefitType
          });

          if (response.success && response.data.valid) {
            let finalAmount = parseFloat(this.amount);

            if (response.data.fixedDiscount > 0) {
              finalAmount = Math.max(0, finalAmount - response.data.fixedDiscount);
            }

            if (response.data.discountPercentage > 0) {
              finalAmount = Math.max(0, finalAmount - (finalAmount * response.data.discountPercentage) / 100);
            }

            this.discountedAmount = finalAmount.toFixed(2);
            this.voucherApplied = true;
            this.courseCheckoutItems[0].price = this.discountedAmount;
            this.courseCheckoutItems[0].description = `${this.title} (Voucher: ${this.voucherCode})`;

            this.logInfo('Voucher applied', {
              original: this.amount,
              discounted: this.discountedAmount
            });
          } else {
            this.voucherErrorMsg = response.data.message || 'Invalid voucher code';
            this.voucherCode = '';
          }
        }
      } catch (error) {
        this.voucherErrorMsg = 'Error validating voucher. Please try again.';
        this.logError('Voucher validation error', error);
      } finally {
        this.voucherLoading = false;
      }
    },
    removeVoucher() {
      // ULC-2669: Prevent removal of invisible vouchers (system-applied)
      if (this.isInvisibleVoucher) {
        console.info('Cannot remove auto-applied voucher');
        return;
      }

      // Prevent voucher removal after transaction has been completed
      if (this.successMsg) {
        this.logInfo('Cannot remove voucher after transaction completion');
        return;
      }

      this.voucherApplied = false;
      this.discountedAmount = null;
      this.voucherCode = '';
      this.voucherErrorMsg = '';
      this.aetherPromoCode = '';
      this.aetherPromoDetails = null;

      // Notify parent to remove promo from Stripe session
      if (this.isStripePaymentEnabled) {
        this.$emit('promo-removed');
      }

      // Reset the checkout item price to original amount
      this.courseCheckoutItems[0].price = this.amount;
      this.courseCheckoutItems[0].description = this.title;

      this.logInfo('Voucher removed');
    },
    onStripePromoApplied(details) {
      this.logInfo('Stripe promo applied on session', details);
    },
    onStripePromoRemoved() {
      this.logInfo('Stripe promo removed from session');
    },

    getPaymentType() {
      return this.buttonAction === 'Add to Transcript' ? 'Credit Conversion' : 'Credit Eligibility';
    },
    async saveInvoiceDetails(enrollmentId) {
      await this.$conn
        .putInvoiceDetails({
          invoice_id: this.invoiceNumber,
          price: this.discountedAmount || this.amount,
          course_id: this.courseInfo._openEdxCourseId,
          transaction_status: 'CLIENT_PENDING',
          user_id: this.userStore.apis.learnerData._asuRite,
          enrollment_id: enrollmentId || this.enrollmentId,
          payment_type: this.getPaymentType(),
          voucher_code: this.voucherApplied ? this.voucherCode : null,
          original_price: this.voucherApplied ? this.amount : null
        })
        .then(() => {});
    },

    prepareCanvasBody(learner) {
      return new CanvasModel(learner);
    },
    closeGradeChangeError() {
      this.showGradeOrModeError = false;

      if (this.from === 'pathways') {
        return this.$router.push('/my-pathway');
      } else {
        return this.$router.push('/dashboard');
      }
    },

    async getGradeChangeResponse() {
      const reqBody = {
        _openEdxCourseId: this.courseInfo._openEdxCourseId,
        _enrollmentId: this.enrollmentId,
        _grade: this.gradeValue,
        _canvasEnrollmentId: this.canvasEnrollmentId ? Number(this.canvasEnrollmentId) : null,
        _canvasUserId: this.canvasUserId ? Number(this.canvasUserId) : null,
        _canvasCourseId: this.canvasCourseId ? Number(this.canvasCourseId) : null
      };

      let gradeChangeRes = await this.$conn.gradeChange(reqBody);

      if (
        gradeChangeRes &&
        gradeChangeRes.length > 0 &&
        gradeChangeRes[0].developer_message === 'Success' &&
        this.gradeValue !== 0
      ) {
        this.continueToPayOrPrepay = true;
      } else if (
        gradeChangeRes &&
        gradeChangeRes.length > 0 &&
        gradeChangeRes[0].developer_message === 'grade_changed'
      ) {
        if (gradeChangeRes[0].grades.grade != 0) {
          this.gradeChanged = true;
          this.continueToPayOrPrepay = true;
          this.localFinalGrade = gradeChangeRes[0].grades.final_grade;
          this.updatedGrade = gradeChangeRes[0].grades.grade;
        } else {
          this.creditConvertModelError = Helper.constants.creditRequestGradeTooLow;
        }
      } else if (gradeChangeRes && gradeChangeRes.length > 0 && gradeChangeRes[0].error_code === 'grade_too_low') {
        this.creditConvertModelError = Helper.constants.creditRequestGradeTooLow;
      } else if (gradeChangeRes && gradeChangeRes.error_code === 'already_credit_requested') {
        this.creditConvertModelError = Helper.constants.alreadyPurchasedMessage;
      } else {
        this.creditConvertModelError = Helper.constants.creditRequestCourseIncorrectlySetup;
      }

      this.showGradeOrModeError = !this.continueToPayOrPrepay;
    },

    async getEnrollmentModeChangeResponse() {
      try {
        // If enrollmentId is 0, default continueToPayOrPrepay to true
        if (this.enrollmentId == 0) {
          this.continueToPayOrPrepay = true;

          return;
        }

        let enrollmentRes = await this.$conn.getEnrollmentById(this.enrollmentId);

        if (
          enrollmentRes?.data?.enrollments &&
          (enrollmentRes.data.enrollments[0] || {}).enrollmentMode === 'verified'
        ) {
          this.continueToPayOrPrepay = false;
          this.creditConvertModelError = Helper.constants.alreadyPurchasedMessage;
        } else {
          this.continueToPayOrPrepay = true;
        }

        this.showGradeOrModeError = !this.continueToPayOrPrepay;
      } catch (error) {
        this.logError('Error fetching enrollment mode', error);
      }
    },

    getStartDate() {
      return this.courseType === 'Continuous' || this.courseType === 'Audit' ? this.today() : this.courseStartDate;
    },

    async paymentAuthorized(event) {
      this.logInfo('Payment Authorized Successfully');
      return event;
    },

    async paymentCompleted(event) {
      this.transactionStatus = 'Success';

      // Emit to parent to show page-wide spinner
      this.$emit('payment-processing', true);

      this.transactions = event.transactions;

      this.logInfo('Payment Completed Successfully', event.transactions);

      await this.transactionEnrollment(event);
    },

    async paymentCancelled(event) {
      this.logInfo('checkout course, logging cancelled payment event', event);

      this.transactionStatus = 'Cancelled';
    },

    // PayPal SDK load / order / capture failures (distinct from a user cancel):
    // surface the payment-error dialog with a contact-support path instead of a
    // silently dead button.
    paymentError(event) {
      this.logInfo('checkout course, logging payment error event', event);

      this.transactionStatus = '';
      this.errorMsg = 'We were unable to process your PayPal payment at this time.';
      this.showPaymentError = true;
    },
    // Once the user pays, or is already BE, then we do this to complete the process.
    // If PayPal was paid, go to dashboard, else continue with BE enrollment
    async continueAfterCheckout() {
      if (this.voucherLoading) {
        return; // Prevent execution if voucher validation is in progress
      }
      if (this.transactionStatus === 'Success' && this.from === 'pathways') {
        this.$router.push('/my-pathway');
      } else if (this.transactionStatus === 'Success') {
        this.$router.push('/dashboard');
      } else if (this.isPrepaidCourseAmount) {
        // Prepaid ($0 — partner already paid) conversions never invoke Stripe: the
        // Stripe checkout is correctly gated for a $0 amount, so Aether's checkout
        // webhook never runs to persist the enrollment/ledger. Complete it ourselves
        // via the benefits-eligible path (same as the legacy PayPal flow) instead of
        // emitting the Stripe 'next', which would render a cosmetic completion screen
        // with nothing persisted.
        await this.transactionEnrollment();
      } else if (this.isStripePaymentEnabled) {
        // When Stripe is enabled, emit to parent so it can trigger StripeCheckout.handlePayment()
        this.$emit('next', { paymentMethod: 'stripe' });
      } else if (this.hasAmountDue) {
        // Legacy PayPal path: payment is collected by the PayPal button, which calls
        // transactionEnrollment via paymentCompleted. Enrolling from here with an
        // amount still due would grant a verified enrollment nobody paid for
        // (ULC-3157), so surface the payment error instead.
        //
        // Unreachable while every learner is on Stripe, but this path is gated by a
        // runtime flag rather than removed: shouldUseAetherFlow is
        // STRIPE_IMPLEMENTATION && (per-learner use_aether_flow || partner rollout),
        // so turning that kill switch off sends all traffic back through here with no
        // deploy. Remove this branch when the PayPal checkout itself is removed.
        this.logError(
          'Continue pressed with payment outstanding; refusing to enroll without payment',
          new Error('CHECKOUT_PAYMENT_OUTSTANDING')
        );
        this.errorMsg = 'Please complete your payment above to finish enrolling in this course.';
        this.showPaymentError = true;
      } else {
        await this.transactionEnrollment();
      }
    },

    // canvasEnrollmentIdLookUp(courseOfferingId) {
    //   let enrollment = this.$store.state.global_course_sections
    //     .map((e) => e.courses.filter((f) => f._courseOfferingId === courseOfferingId))
    //     .filter((g) => g.length > 0);

    //   return enrollment.flat(1)[0]._canvasEnrollmentId;
    // },

    async transactionEnrollment(paypalResponse = {}) {
      const self = this;
      try {
        const enrollmentMode = this.buttonAction === 'Add to Transcript' ? 'credit' : 'verified';

        // Determine payment mode based on voucher and amount
        let paymentMode;
        if (this.voucherApplied && parseFloat(this.discountedAmount) === 0) {
          // If voucher makes it free
          paymentMode = 'free';

          // Generate transaction data for free voucher enrollments if not provided
          if (!paypalResponse.id) {
            paypalResponse = {
              id: 'free-' + this.invoiceNumber,
              paymentMode: 'free',
              transactions: [
                {
                  invoice_number: this.invoiceNumber,
                  amount: { total: '0.00' }
                }
              ]
            };
          }
        } else if (this.isPrepaidCourseAmount) {
          // If benefits eligible
          paymentMode = 'benefitsEligible';
        } else if (this.isStripePaymentEnabled) {
          paymentMode = 'stripe';
        } else {
          // Default to paypal
          paymentMode = 'paypal';
        }

        let resolvedEnrollmentId = this.enrollmentId;

        try {
          // Step 1: Handle enrollment based on type (Vue 2 logic)
          if (this.enrollmentType === 'new') {
            // For new enrollments, create course connection in Salesforce
            const selectedCourse = {
              _courseOfferingId: this.courseOfferingId,
              _enrollmentMode: enrollmentMode,
              _isSelected: true,
              _courseInfo: this.courseInfo,
              _enrollmentType: this.apiEnrollmentType
            };

            const enrollments = await this.$conn.createCourseConnectionInSalesforce([selectedCourse]);
            resolvedEnrollmentId = enrollments?.[0]?.enrollmentId || this.enrollmentId;
          } else {
            // For existing enrollments, patch enrollment data
            const patchData = [
              {
                enrollmentId: String(this.enrollmentId), // Ensure it's a string
                enrollmentMode
              }
            ];

            await this.$conn.patchEnrollmentsData(patchData);
          }
        } catch (error) {
          this.logError(
            this.enrollmentType === 'new'
              ? 'Create course connection failed during checkout'
              : 'Patch failed during checkout',
            error
          );
          throw error;
        }

        // Save invoice with real enrollment ID (covers PayPal, free voucher, and benefits-eligible flows)
        // Non-blocking: invoice save should not prevent ledger creation or credit request
        // since the student has already paid by this point
        try {
          await this.saveInvoiceDetails(resolvedEnrollmentId);
        } catch (invoiceError) {
          this.logError('Invoice save failed, continuing with transaction and credit request', invoiceError);
        }

        const enrollmentRequestPayload = {
          _courseInfo: this.courseInfo,
          _enrollmentStatus: 'Active',
          _enrollmentMode: enrollmentMode,
          _benefitsEligible: this.benefitsEligible,
          _benefitType: this.benefitType,
          _amount: parseFloat(this.amount),
          _creditConversionPrice: parseFloat(this.creditConversionPrice) || 0
        };

        // Add courseOfferingName if openEdxCourseId is missing
        if (!enrollmentRequestPayload._courseInfo?._openEdxCourseId && this.courseOfferingName) {
          enrollmentRequestPayload._courseInfo._openEdxCourseId = this.courseOfferingName;
        }

        // Include voucher information if a voucher is applied (including invisible vouchers)
        if (this.voucherApplied && this.voucherCode) {
          enrollmentRequestPayload._voucherCode = this.voucherCode;
          enrollmentRequestPayload._voucherDiscount = parseFloat(this.amount) - parseFloat(this.discountedAmount);
          enrollmentRequestPayload._finalAmount = parseFloat(this.discountedAmount);
          // ULC-2669: Track if this was an invisible (auto-applied) voucher
          enrollmentRequestPayload._isInvisibleVoucher = this.isInvisibleVoucher;
        }

        // December 2024 Early Conversion Campaign (ULC-2637)
        // Include campaign code if this is a campaign-eligible transaction
        if (this.courseInfo?.campaignEligible && this.courseInfo?.campaignCode) {
          enrollmentRequestPayload._campaignCode = this.courseInfo.campaignCode;
        }

        // Step 3: Fail open scenario, create a Ledger
        // Skip for the Stripe flow — Aether creates the ledger via its checkout
        // webhook and notifies ULC directly. But a prepaid ($0) conversion is
        // gated out of Stripe entirely, so Aether never runs: in that case we
        // must still post the ledger ourselves (benefits-eligible), matching the
        // legacy PayPal flow.
        let transactionResponse;
        if (this.isStripePaymentEnabled && !this.isPrepaidCourseAmount) {
          this.logInfo('Skipping postTransaction — Aether handles ledger creation for Stripe flow');
        } else {
          transactionResponse = await this.$conn.postTransaction(enrollmentRequestPayload, paymentMode, paypalResponse);
        }

        // The server rejects a benefits-eligible claim it cannot corroborate and
        // books the amount owed instead (ULC-3157). Prompt for payment rather than
        // showing a completed enrollment the learner has not paid for.
        // postTransaction resolves the axios response, so the API payload is under `.data`.
        if (transactionResponse?.data?.paymentRequired) {
          this.logError(
            'Server could not verify benefits coverage; payment is required',
            new Error(transactionResponse.data.code || 'BENEFITS_CLAIM_UNVERIFIED')
          );
          this.transactionStatus = '';
          this.errorMsg = 'This course is not covered by your program. Please contact support before enrolling.';
          this.showPaymentError = true;
          this.$emit('payment-complete');

          return;
        }

        if (paymentMode === 'free') {
          // For free voucher enrollments and benefitsEligible, emit the transaction data
          this.transactionStatus = 'Success';
          this.$emit('payment-complete');
          this.$emit('next', {
            id: transactionResponse.data?.id || paypalResponse.id,
            paymentMode: paymentMode, // This is crucial - ensure paymentMode is passed correctly
            transactions: paypalResponse.transactions || [
              {
                invoice_number: this.invoiceNumber,
                amount: { total: '0.00' }
              }
            ]
          });
        }

        if (enrollmentMode === 'credit') {
          // Check feature flag for new credit request flow
          const useNewCreditFlow = isFeatureEnabled('NEW_CREDIT_FLOW');

          let creditRequestResponse;

          if (useNewCreditFlow) {
            // New flow: Skip credit request service call, AddToTranscript component will handle it
            creditRequestResponse = {
              request_uuid: Date.now().toString() // Temporary UUID for flow compatibility
            };
          } else {
            // Old flow: Use existing CreditRequestService
            creditRequestResponse = await CreditRequestService.sendCreditRequest({
              _finalGrade: this.localFinalGrade || this.finalGrade,
              _grade: isNaN(this.gradeValue) ? this.gradeValue : this.gradeValue.toFixed(2),
              _courseCompletedDate: this.courseCompletedDate,
              _enrollmentDate: this.enrollmentDate,
              _enrollmentId: this.enrollmentId,
              _courseInfo: {
                _openEdxCourseId: this.courseInfo._openEdxCourseId,
                _courseOrg: this.courseInfo._courseOrg,
                _courseRun: this.courseInfo._courseRun,
                _courseNum: this.courseInfo._courseNum,
                _lms: this.courseInfo._lms
              }
            });
          }

          if (creditRequestResponse.errorMessage) {
            this.showPaymentError = true;
            this.errorMsg = 'The credit request was previously submitted.';
          }

          if (creditRequestResponse && creditRequestResponse.request_uuid) {
            this.$emit('payment-complete');

            // Check feature flag for new credit request flow
            // NEW_CREDIT_FLOW=e: Use new AddToTranscript component with v2 API
            // NEW_CREDIT_FLOW=d: Use legacy credit-request-form component
            const useNewCreditFlow = isFeatureEnabled('NEW_CREDIT_FLOW');

            if (useNewCreditFlow) {
              // New flow: Redirect to AddToTranscript component with asurite and enrollmentId
              const asurite = this.userStore.apis.learnerData._asuRite;
              const enrollmentId = this.enrollmentId;

              return this.$router.push({
                path: '/add-to-transcript',
                query: {
                  asurite: asurite,
                  enrollmentId: enrollmentId
                }
              });
            } else {
              // Old flow: Show credit request form
              return this.callCreditRequestChildComponent(creditRequestResponse);
            }
          }
          this.$emit('payment-complete');
        } else {
          self.transactionStatus = 'Success';

          if (this.isPrepaidCourseAmount) {
            this.$emit('payment-complete');
            return this.$router.push('/dashboard');
          }

          // Clear loader and emit completion before sending to parent

          this.$emit('payment-complete');
          this.$emit('next', paypalResponse);

          //OS-4970: To avoid duplicate payment when checkout page opened in 2 tabs for same course (capturing in localstorage)
          localStorage.setItem('paid_' + this.enrollmentId, true);
        }
      } catch (error) {
        this.logError('Failed to capture payment, need to retry saving em and transactiond details', error);

        this.$emit('payment-complete');
      }
    },

    callCreditRequestChildComponent(response) {
      this.creditRequestRequestUUID = response['request_uuid'];
      this.creditRequestCourseOrg = response['course_org'];
      this.creditRequestCourseRun = response['course_run'];
      this.creditRequestCourseNum = response['course_num'];
      this.creditRequestTimestamp = response['timestamp'];
      this.creditRequestUserUsername = response['user_username'];
      this.creditRequestUserEmail = response['user_email'];
      this.creditRequestUserFullName = response['user_full_name'];
      this.creditRequestUserMailingAddress = response['user_mailing_address'];
      this.creditRequestUserCountry = response['user_country'];
      this.creditRequestSignature = response['signature'];
      this.creditRequestEnrollmentTimestamp = response['enrollment_timestamp'];
      this.creditRequestCourseCompletionTimestamp = response['course_completion_timestamp'];
      this.creditRequestFinalGrade = response['final_grade'];

      this.shouldLoadCreditRequestForm = true;
    },

    async handleContinueClick() {
      if (this.voucherLoading || this.campaignCheckLoading) return;

      this.asuoDataLayerClick('courseCheckout', '2', {
        component: this.coursePrefixDisplayLabel || this.coursePrefix + ' ' + this.title
      });

      // Only run campaign check if not already passed
      if (this.buttonLabel === 'Add to Transcript' && this.courseInfo?.campaignEligible && !this.campaignCheckPassed) {
        if (connectorService.learnerData?.learnerId) {
          this.campaignCheckLoading = true;
          try {
            const usageCheck = await connectorService.checkCampaignUsage(connectorService.learnerData.learnerId);

            if (usageCheck.campaignActive && usageCheck.alreadyUsed) {
              // Block PayPal and show error - user already used campaign discount
              this.showGradeOrModeError = true;
              this.creditConvertModelError = 'You have already used the $350 campaign discount.';
              console.info(`Campaign usage blocked - already used in transaction ${usageCheck.transactionId}`);
              this.campaignCheckLoading = false;
              return; // Stop here - don't enable PayPal
            }
            // Campaign check passed - enable PayPal
            this.campaignCheckPassed = true;
          } catch (error) {
            console.error('Error checking campaign usage before payment:', error);
            // On error, proceed (fail open) - hard check in backend will catch issues
            this.campaignCheckPassed = true;
          }
          this.campaignCheckLoading = false;
          // Return here - user needs to click Continue again to proceed with PayPal now visible
          return;
        } else {
          this.campaignCheckPassed = true;
        }
      }

      if (this.buttonLabel === 'Add to Transcript') {
        if (this.finalGradeCheck) {
          if (this.continueToPayOrPrepay) {
            this.continueAfterCheckout();
          } else {
            this.showGradeOrModeError = true;
          }
        }
      } else {
        this.continueAfterCheckout();
      }
    }
  },

  async created() {
    // Initialize checkout store
    this.checkoutStore = useCheckoutStore();

    // Initialize courseCheckoutItems now that store data is available
    this.courseCheckoutItems[0].name = this.coursePrefix;
    this.courseCheckoutItems[0].description = this.title;

    // Triggers the ULC pre-checkout aether_enrollment_id backfill for this
    // specific enrollment so a Stripe checkout that follows can resolve the
    // ULC↔Aether link in handleAetherLedgerCallback. The verified-upgrade
    // path already calls this via getEnrollmentModeChangeResponse, but the
    // credit-conversion path uses getGradeChangeResponse only — without this
    // explicit call, ULC's enrollment row stays unstamped on a verified→credit
    // checkout. Side-effect only; response is intentionally discarded.
    if (this.enrollmentId && this.enrollmentId != 0) {
      try {
        await this.$conn.getEnrollmentById(this.enrollmentId);
      } catch (err) {
        this.logError('Pre-checkout enrollment fetch failed (non-blocking)', err);
      }
    }

    // For non-campaign flows, PayPal should be enabled immediately
    // Campaign check only needed for 'Add to Transcript' with campaignEligible course
    if (this.buttonAction !== 'Add to Transcript' || !this.courseInfo?.campaignEligible) {
      this.campaignCheckPassed = true;
    }

    if (this.buttonAction === 'Add to Transcript') {
      this.amount = this.creditConversionPrice ? this.creditConversionPrice + '' : '0';
      this.courseCheckoutItems[0].price = this.creditConversionPrice ? this.creditConversionPrice + '' : '0';

      // ULC-2669: Check for invisible voucher auto-apply
      if (this.isStripePaymentEnabled) {
        // Aether invisible promo code lookup
        try {
          const invisibleResult = await this.$conn.findInvisiblePromoCodes({
            benefitType: this.benefitType,
            purchaseType: 'credit_conversion',
            courseOfferingId: this.courseOfferingId,
            learnerId: this.userStore.apis.learnerData?.learnerId,
            term: this.courseInfo?.term,
            partnerName: this.userStore.apis.learnerData?._partnerName
          });

          const promoData = invisibleResult?.data || invisibleResult;

          if (promoData?.valid && promoData.code) {
            this.voucherCode = promoData.code;
            this.voucherApplied = true;
            this.isInvisibleVoucher = true;
            this.aetherPromoCode = promoData.code;
            this.aetherPromoDetails = promoData;

            if (promoData.coupon) {
              let finalAmount = parseFloat(this.amount);
              if (promoData.coupon.amount_off) {
                finalAmount = Math.max(0, finalAmount - promoData.coupon.amount_off / 100);
              }
              if (promoData.coupon.percent_off) {
                finalAmount = Math.max(0, finalAmount - (finalAmount * promoData.coupon.percent_off) / 100);
              }
              this.discountedAmount = finalAmount.toFixed(2);
            }

            this.courseCheckoutItems[0].price = this.discountedAmount || this.amount;
            this.courseCheckoutItems[0].description = `${this.title} (Voucher: ${promoData.code})`;

            this.$emit('promo-validated', {
              code: promoData.code,
              coupon: promoData.coupon,
              campaignCode: promoData.campaign_code
            });

            console.info(`Aether invisible promo auto-applied: ${promoData.code}`);
          }
        } catch (error) {
          console.error('Error looking up invisible promo codes:', error);
        }
      } else if (this.courseInfo?.invisibleVoucher?.available) {
        // Legacy invisible voucher flow
        const iv = this.courseInfo.invisibleVoucher;
        this.voucherCode = iv.voucherCode;
        this.voucherApplied = true;
        this.isInvisibleVoucher = true;
        this.amount = iv.originalPrice + '';
        this.discountedAmount = iv.finalPrice + '';
        this.courseCheckoutItems[0].price = iv.finalPrice + '';
        this.courseCheckoutItems[0].description = `${this.title} (Voucher: ${iv.voucherCode})`;
        console.info(`Invisible voucher auto-applied: ${iv.voucherCode}, ${iv.originalPrice} -> ${iv.finalPrice}`);
      }

      await this.getGradeChangeResponse();
    } else {
      this.amount = this.creditEligibilityPrice ? this.creditEligibilityPrice + '' : '0';
      this.courseCheckoutItems[0].price = this.creditEligibilityPrice ? this.creditEligibilityPrice + '' : '0';

      this.continueToPayOrPrepay = true;

      await this.getEnrollmentModeChangeResponse();
    }

    if (this.errorMessage) {
      this.showGradeOrModeError = true;
      this.creditConvertModelError = this.errorMessage;
      return;
    }

    // December 2024 Early Conversion Campaign (ULC-2637)
    // Check on page load to determine correct pricing

    if (
      this.buttonAction === 'Add to Transcript' &&
      this.courseInfo?.campaignEligible &&
      connectorService.learnerData?.learnerId
    ) {
      try {
        const usageCheck = await connectorService.checkCampaignUsage(connectorService.learnerData.learnerId);

        if (usageCheck.campaignActive && usageCheck.alreadyUsed) {
          // Revert to original price ($400) - user already used campaign discount
          const originalPrice = this.courseInfo.creditConversionOriginalPrice || 400;
          this.amount = originalPrice + '';
          this.courseCheckoutItems[0].price = originalPrice + '';
        }
      } catch (error) {
        console.error('Error checking campaign usage on page load:', error);
        // On error, keep the price as-is (fail open)
      }
    }
  },

  mounted() {
    // Initialize local final grade from prop
    this.localFinalGrade = this.finalGrade;
    let i = setInterval(() => {
      //OS-4970: to check whether payment for enrollment already done in other tab, if yes showing modal to indicate already purchased
      if (localStorage.getItem('paid_' + this.enrollmentId) === 'true') {
        this.continueToPayOrPrepay = false;

        if (!this.successMsg) {
          this.creditConvertModelError = Helper.constants.alreadyPurchasedMessage;
          this.showGradeOrModeError = true;
        }

        clearInterval(i);
      }
    }, 100);
  }
});
</script>
